// ============================================================
// FEATURE FLAGS — Fase 7 + Fase 9 (Vision)
// Controle fino de funcionalidades operacionais
// Fonte de verdade: env vars + localStorage override (dev)
// ============================================================

export interface FeatureFlags {
  // Operational AI
  ENABLE_AI_ALERTS:          boolean
  ENABLE_PREDICTIVE_ENGINE:  boolean
  ENABLE_ANOMALY_DETECTION:  boolean

  // Digital twin / visual
  ENABLE_DIGITAL_TWIN:       boolean
  ENABLE_INTERACTIVE_MAP:    boolean
  ENABLE_WORKFORCE_HEATMAP:  boolean

  // Data engines
  ENABLE_SUPPLY_ENGINE:      boolean
  ENABLE_RDO_SYNC:           boolean
  ENABLE_PLANNING_ENGINE:    boolean
  ENABLE_PRODUCTION_ENGINE:  boolean
  ENABLE_SAFETY_ENGINE:      boolean

  // TV / display
  ENABLE_CONTEXTUAL_PLAYLISTS:  boolean
  ENABLE_EMERGENCY_INTERRUPTION: boolean
  ENABLE_TV_SNAPSHOTS:          boolean

  // Fase 9 — Computer Vision
  ENABLE_CAMERA_WALL:        boolean
  ENABLE_PPE_DETECTION:      boolean
  ENABLE_DRONE_INTELLIGENCE: boolean
  ENABLE_VISION_AI:          boolean
  ENABLE_PROGRESS_VISION:    boolean
  ENABLE_MOTION_DETECTION:   boolean
  ENABLE_OCR_SERVICE:        boolean
  ENABLE_VISION_TV:          boolean

  // Debug
  ENABLE_RISK_DEBUG:         boolean
  ENABLE_PERF_OVERLAY:       boolean
}

// ─── Defaults ────────────────────────────────────────────────
const DEFAULTS: FeatureFlags = {
  ENABLE_AI_ALERTS:              true,
  ENABLE_PREDICTIVE_ENGINE:      true,
  ENABLE_ANOMALY_DETECTION:      true,

  ENABLE_DIGITAL_TWIN:           false,   // beta — off by default
  ENABLE_INTERACTIVE_MAP:        true,
  ENABLE_WORKFORCE_HEATMAP:      true,

  ENABLE_SUPPLY_ENGINE:          true,
  ENABLE_RDO_SYNC:               true,
  ENABLE_PLANNING_ENGINE:        true,
  ENABLE_PRODUCTION_ENGINE:      true,
  ENABLE_SAFETY_ENGINE:          true,

  ENABLE_CONTEXTUAL_PLAYLISTS:   true,
  ENABLE_EMERGENCY_INTERRUPTION: true,
  ENABLE_TV_SNAPSHOTS:           true,

  // Fase 9 — Computer Vision (beta — off by default until cameras are provisioned)
  ENABLE_CAMERA_WALL:            false,
  ENABLE_PPE_DETECTION:          false,
  ENABLE_DRONE_INTELLIGENCE:     false,
  ENABLE_VISION_AI:              false,
  ENABLE_PROGRESS_VISION:        false,
  ENABLE_MOTION_DETECTION:       false,
  ENABLE_OCR_SERVICE:            false,
  ENABLE_VISION_TV:              false,

  ENABLE_RISK_DEBUG:             false,
  ENABLE_PERF_OVERLAY:           false,
}

// ─── Env overrides ───────────────────────────────────────────
function fromEnv(): Partial<FeatureFlags> {
  const out: Partial<FeatureFlags> = {}
  const prefix = 'VITE_FF_'
  for (const [key, def] of Object.entries(DEFAULTS)) {
    const envKey = `${prefix}${key}`
    const val = import.meta.env[envKey]
    if (val === 'true')  (out as Record<string, boolean>)[key] = true
    if (val === 'false') (out as Record<string, boolean>)[key] = false
  }
  return out
}

// ─── Dev localStorage override (only in dev mode) ────────────
function fromStorage(): Partial<FeatureFlags> {
  if (import.meta.env.PROD) return {}
  try {
    const raw = localStorage.getItem('canteiro:ff')
    if (!raw) return {}
    return JSON.parse(raw) as Partial<FeatureFlags>
  } catch {
    return {}
  }
}

// ─── Merged singleton ────────────────────────────────────────
let _flags: FeatureFlags | null = null

export function getFlags(): FeatureFlags {
  if (_flags) return _flags
  _flags = { ...DEFAULTS, ...fromEnv(), ...fromStorage() }
  return _flags
}

/** Override a flag at runtime (dev only). Persists to localStorage. */
export function setFlag<K extends keyof FeatureFlags>(
  key: K,
  value: FeatureFlags[K],
): void {
  if (import.meta.env.PROD) return
  const current = fromStorage()
  current[key] = value
  localStorage.setItem('canteiro:ff', JSON.stringify(current))
  _flags = null  // invalidate cache
  console.info(`[FF] ${key} = ${value}`)
}

/** Reset all dev overrides */
export function resetFlags(): void {
  localStorage.removeItem('canteiro:ff')
  _flags = null
}

export const FF = getFlags()
